/**
 * 权限工具函数
 */

import type { UserInfo, Role, Permission, PermissionCheckParams } from '@/types/auth';
import { getStorageItem, STORAGE_KEYS } from './storage';

type StoredUserInfo = Omit<UserInfo, 'roles' | 'permissions'> & {
  // AuthContext persists the selected tenant in the flattened login-response shape.
  tenant_id?: ID;
  role_codes?: string[];
  roles?: Array<Role | string | { code?: string; roleCode?: string; role_code?: string; dataScope?: number }>;
  permissions?: Array<Permission | string>;
};

type PermissionNode = {
  meta?: Pick<PermissionCheckParams, 'roles' | 'permissions'>;
  children?: PermissionNode[];
};

/**
 * 获取当前用户信息
 */
export const getCurrentUser = (): StoredUserInfo | null => {
  return getStorageItem<StoredUserInfo | null>(STORAGE_KEYS.USER_INFO, null);
};

/**
 * 获取当前用户角色
 */
export const getCurrentUserRoles = (): string[] => {
  const user = getCurrentUser();
  if (!user) return [];
  // 优先使用扁平化的 role_codes（AuthContext 登录时已展开）。旧登录缓存可能
  // 因未识别 roles[].role_code 而存成空数组，此时必须继续回退解析 roles。
  if (Array.isArray(user.role_codes) && user.role_codes.length > 0) {
    return user.role_codes.filter(Boolean);
  }
  if (!user.roles) return [];
  return user.roles
    .map((role) => {
      if (typeof role === 'string') return role;
      return ('roleCode' in role ? role.roleCode : undefined)
        || ('role_code' in role ? role.role_code : undefined)
        || role.code
        || '';
    })
    .filter(Boolean);
};

/**
 * 获取当前用户权限
 */
export const getCurrentUserPermissions = (): string[] => {
  const user = getCurrentUser();
  if (!user || !user.permissions) return [];
  // 兼容 string[] 和 Permission[] 格式
  return user.permissions
    .map((permission) => (typeof permission === 'string' ? permission : permission.code))
    .filter(Boolean);
};

/**
 * 检查是否有指定角色
 * @param roles 角色代码或角色代码数组
 * @param mode 检查模式：all-需要全部满足，any-满足任一即可
 */
export const hasRole = (
  roles: string | string[],
  mode: 'all' | 'any' = 'any'
): boolean => {
  const userRoles = getCurrentUserRoles();
  
  if (userRoles.length === 0) return false;
  
  const targetRoles = Array.isArray(roles) ? roles : [roles];
  
  if (mode === 'all') {
    return targetRoles.every(role => userRoles.includes(role));
  } else {
    return targetRoles.some(role => userRoles.includes(role));
  }
};

/**
 * 检查是否有指定权限
 * @param permissions 权限代码或权限代码数组
 * @param mode 检查模式：all-需要全部满足，any-满足任一即可
 */
export const hasPermission = (
  permissions: string | string[],
  mode: 'all' | 'any' = 'any'
): boolean => {
  // superadmin 绕过权限检查
  if (isSuperAdmin()) return true;

  const userPermissions = getCurrentUserPermissions();

  if (userPermissions.length === 0) return false;

  const targetPermissions = Array.isArray(permissions) ? permissions : [permissions];

  if (mode === 'all') {
    return targetPermissions.every(permission => userPermissions.includes(permission));
  } else {
    return targetPermissions.some(permission => userPermissions.includes(permission));
  }
};

/**
 * 检查权限（支持角色和权限混合检查）
 * @param params 权限检查参数
 */
export const checkPermission = (params: PermissionCheckParams): boolean => {
  // 只允许超级管理员直接通过
  if (isSuperAdmin()) {
    return true;
  }
  
  const { roles, permissions, mode = 'any' } = params;
  
  let roleCheck = true;
  let permissionCheck = true;
  
  // 检查角色
  if (roles) {
    roleCheck = hasRole(roles, mode);
  }
  
  // 检查权限
  if (permissions) {
    permissionCheck = hasPermission(permissions, mode);
  }
  
  // 如果同时指定了角色和权限
  if (roles && permissions) {
    if (mode === 'all') {
      return roleCheck && permissionCheck;
    } else {
      return roleCheck || permissionCheck;
    }
  }
  
  // 只指定了角色
  if (roles) {
    return roleCheck;
  }
  
  // 只指定了权限
  if (permissions) {
    return permissionCheck;
  }
  
  // 都没指定，默认允许
  return true;
};

/**
 * 角色名白名单：命中即被视为「超级管理员」，在 `hasPermission` / `Permission`
 * 组件等权限检查里被早返回（bypass）。
 *
 * REQ-20260521-0011：补 `tenant_full_admin`。migration 056 设计该角色为
 * 「租户全功能管理员」（语义比 admin 更高），但前端白名单原本没收录，
 * 导致该 role 的用户登录后在「访问控制 → 角色管理」看不到 CRUD 按钮
 * （前端按钮码 `system:roles:*` 与 DB seed `tenant:role:*` 不对齐，
 * 只有走 bypass 才能显示）。
 */
const SUPER_ADMIN_ROLE_CODES: ReadonlyArray<string> = [
  'super_admin',
  'superadmin',      // 李宁租户超管角色（DB role code 无下划线）
  'system_admin',
  'admin', // 租户管理员，在租户范围内拥有全部权限
  'tenant_full_admin', // 租户全功能管理员（REQ-20260521-0011 sediment）
];

/**
 * 检查是否为超级管理员
 */
export const isSuperAdmin = (): boolean => {
  const user = getCurrentUser();
  // platform 用户视为超级管理员（兼容 snake_case 和 camelCase）
  if (user?.user_scope === 'platform' || user?.userScope === 'platform') return true;
  const userRoles = getCurrentUserRoles();
  return userRoles.some((r) => SUPER_ADMIN_ROLE_CODES.includes(r));
};

/**
 * 平台技术人员角色码：与后端 jst middleware.RequirePlatformAdmin 的白名单对齐。
 * 仅这些身份可维护聚水潭「应用」（平台级共享资源）；根租户/客户仅只读与使用。
 */
const PLATFORM_ADMIN_ROLE_CODES: ReadonlyArray<string> = [
  'platform_admin',
  'super_admin',
  'superadmin',
];

/**
 * 检查是否为平台技术人员（platform scope 或平台级角色）。
 * 仅作 UI 收口（隐藏应用写按钮）；真正的越权防护由后端 RequirePlatformAdmin 兜底。
 */
export const isPlatformAdmin = (): boolean => {
  const user = getCurrentUser();
  if (user?.user_scope === 'platform' || user?.userScope === 'platform') return true;
  const userRoles = getCurrentUserRoles();
  return userRoles.some((r) => PLATFORM_ADMIN_ROLE_CODES.includes(r));
};

/**
 * 客户（子租户）管理员角色码：经销商/客户主体的管理者。
 * 这些账号在「员工管理」里只能选用客户适用角色，且不应看到角色/菜单管理入口。
 */
const CUSTOMER_ADMIN_ROLE_CODES: ReadonlyArray<string> = [
  'tenant_full_admin', // 一级经销商等客户主体管理员（6 个一级经销商账号即此角色）
  'customer', // 客户角色
];

/**
 * 品牌方/平台全功能管理员角色码：命中即视为非客户管理员（看全量角色/菜单）。
 * 注意：不含 `tenant_full_admin`（它在 SUPER_ADMIN_ROLE_CODES 里用于权限 bypass，
 * 但在「客户 vs 品牌方」语义上属于客户主体），也不含 `admin`（品牌方租户管理员
 * 自身不是客户，但单凭 `admin` 也不归类为客户管理员——见下方判别仅取 customer 码集）。
 */
const BRAND_OR_PLATFORM_ADMIN_ROLE_CODES: ReadonlyArray<string> = [
  'super_admin',
  'system_admin',
  'platform_admin',
];

/**
 * 是否为客户（子租户）管理员。
 *
 * REQ-20260615-0009：客户员工账号管理——区分品牌方 admin 与客户(子租户) admin。
 *
 * ⚠️ JWT payload 无 `parent_id` / 子租户标记（见 CLAUDE.md「前端身份判别陷阱」），
 * 故只能用 `user_scope` + `roles` 判别，且**仅作 UI 收口**；真正的越权防护由后端
 * foundation `validateAssignableRoles`（仅 tenant.parent_id != 0 子租户受限）兜底。
 *
 * 判别：
 *   - platform 作用域 → false（平台管理员）
 *   - 含 super_admin/system_admin/platform_admin/admin → false（品牌方/平台全功能管理员）
 *   - 含 tenant_full_admin / customer → true（客户/经销商主体管理员）
 *   - 其它 → false
 */
export const isCustomerAdmin = (): boolean => {
  const user = getCurrentUser();
  if (user?.user_scope === 'platform' || user?.userScope === 'platform') return false;
  const userRoles = getCurrentUserRoles();
  if (userRoles.some((r) => BRAND_OR_PLATFORM_ADMIN_ROLE_CODES.includes(r))) return false;
  return userRoles.some((r) => CUSTOMER_ADMIN_ROLE_CODES.includes(r));
};

/**
 * 是否允许管理当前租户的系统公告。
 *
 * 公告面向整个租户发布，因此只开放给品牌方或平台管理员。经销商主体的
 * tenant_full_admin 虽会绕过部分通用前端权限检查，但不属于公告发布角色。
 */
export const canManageAnnouncements = (): boolean => {
  const user = getCurrentUser();
  if (user?.user_scope === 'platform' || user?.userScope === 'platform') return true;

  const managerRoleCodes = [
    'superadmin',
    'super_admin',
    'system_admin',
    'platform_admin',
    'tenant_admin',
    'admin',
  ];
  return getCurrentUserRoles().some((role) => managerRoleCodes.includes(role));
};

/**
 * 检查是否已登录
 */
export const isAuthenticated = (): boolean => {
  const user = getCurrentUser();
  const token = getStorageItem(STORAGE_KEYS.TOKEN);
  return !!(user && token);
};

/**
 * 检查用户状态是否正常
 */
export const isUserActive = (): boolean => {
  const user = getCurrentUser();
  return user ? user.status === 1 : false;
};

/**
 * 检查租户状态是否正常
 */
export const isTenantActive = (): boolean => {
  const user = getCurrentUser();
  if (!user || !user.tenant) return true; // 没有租户信息时默认允许
  
  const tenant = user.tenant;
  
  // 检查租户状态
  if (tenant.status !== 1) return false;
  
  // 检查租户是否过期
  if (tenant.expireTime) {
    const expireTime = new Date(tenant.expireTime);
    const now = new Date();
    if (now > expireTime) return false;
  }
  
  return true;
};

/**
 * 获取用户数据权限范围
 */
export const getDataScope = (): number => {
  const user = getCurrentUser();
  if (!user || !user.roles || user.roles.length === 0) return 5; // 默认仅本人
  const roles = user.roles.filter((role) => typeof role !== 'string');
  if (roles.length === 0) return 5;
  
  // 取最大权限范围（数值越小权限越大）
  return Math.min(...roles.map((role) => role.dataScope || 5));
};

/**
 * 检查是否有数据权限
 * @param targetUserId 目标用户ID
 * @param targetDeptId 目标部门ID
 */
export const hasDataPermission = (
  targetUserId?: string | number,
  _targetDeptId?: string | number
): boolean => {
  const user = getCurrentUser();
  if (!user) return false;
  
  // 超级管理员有所有权限
  if (isSuperAdmin()) return true;
  
  const dataScope = getDataScope();
  
  switch (dataScope) {
    case 1: // 全部数据权限
      return true;
    
    case 2: // 自定义数据权限
      // TODO: 实现自定义数据权限逻辑
      return true;
    
    case 3: // 部门数据权限
      // TODO: 检查是否为同一部门
      return true;
    
    case 4: // 部门及以下数据权限
      // TODO: 检查是否为部门及其子部门
      return true;
    
    case 5: // 仅本人数据权限
      return targetUserId ? String(targetUserId) === String(user.id) : false;
    
    default:
      return false;
  }
};

/**
 * 过滤有权限的菜单
 * @param menus 菜单列表
 */
export const filterMenusByPermission = (menus: PermissionNode[]): PermissionNode[] => {
  if (!menus || menus.length === 0) return [];
  
  return menus.filter(menu => {
    // 检查菜单权限
    if (menu.meta && (menu.meta.roles || menu.meta.permissions)) {
      const hasAuth = checkPermission({
        roles: menu.meta.roles,
        permissions: menu.meta.permissions,
        mode: 'any'
      });
      
      if (!hasAuth) return false;
    }
    
    // 递归过滤子菜单
    if (menu.children && menu.children.length > 0) {
      menu.children = filterMenusByPermission(menu.children);
    }
    
    return true;
  });
};

/**
 * 过滤有权限的路由
 * @param routes 路由列表
 */
export const filterRoutesByPermission = (routes: PermissionNode[]): PermissionNode[] => {
  if (!routes || routes.length === 0) return [];
  
  return routes.filter(route => {
    // 检查路由权限
    if (route.meta && (route.meta.roles || route.meta.permissions)) {
      const hasAuth = checkPermission({
        roles: route.meta.roles,
        permissions: route.meta.permissions,
        mode: 'any'
      });
      
      if (!hasAuth) return false;
    }
    
    // 递归过滤子路由
    if (route.children && route.children.length > 0) {
      route.children = filterRoutesByPermission(route.children);
    }
    
    return true;
  });
};

/**
 * 检查路由访问权限
 * @param route 路由对象
 */
export const checkRoutePermission = (route: PermissionNode): boolean => {
  if (!route) return false;
  
  // 检查用户是否已登录
  if (!isAuthenticated()) return false;
  
  // 检查用户状态
  if (!isUserActive()) return false;
  
  // 检查租户状态
  if (!isTenantActive()) return false;
  
  // 检查路由权限
  if (route.meta && (route.meta.roles || route.meta.permissions)) {
    return checkPermission({
      roles: route.meta.roles,
      permissions: route.meta.permissions,
      mode: 'any'
    });
  }
  
  return true;
};

/**
 * 获取权限错误信息
 */
export const getPermissionErrorMessage = (): string => {
  if (!isAuthenticated()) {
    return '请先登录';
  }
  
  if (!isUserActive()) {
    return '用户状态异常，请联系管理员';
  }
  
  if (!isTenantActive()) {
    return '租户状态异常或已过期，请联系管理员';
  }
  
  return '没有访问权限';
};

/**
 * 权限装饰器工厂
 * @param params 权限检查参数
 */
export const withPermission = (params: PermissionCheckParams) => {
  return function <T extends (...args: never[]) => unknown>(
    _target: object,
    propertyKey: string,
    descriptor: TypedPropertyDescriptor<T>
  ) {
    const originalMethod = descriptor.value;
    
    descriptor.value = function (this: unknown, ...args: Parameters<T>) {
      if (!checkPermission(params)) {
        console.warn(`权限检查失败: ${propertyKey}`);
        return;
      }
      
      return originalMethod?.apply(this, args);
    } as T;
    
    return descriptor;
  };
};

/**
 * 权限工具类
 */
export class PermissionUtil {
  /**
   * 检查角色
   */
  static hasRole = hasRole;
  
  /**
   * 检查权限
   */
  static hasPermission = hasPermission;
  
  /**
   * 综合权限检查
   */
  static check = checkPermission;
  
  /**
   * 是否为超级管理员
   */
  static isSuperAdmin = isSuperAdmin;
  
  /**
   * 是否已认证
   */
  static isAuthenticated = isAuthenticated;
  
  /**
   * 过滤菜单
   */
  static filterMenus = filterMenusByPermission;
  
  /**
   * 过滤路由
   */
  static filterRoutes = filterRoutesByPermission;
  
  /**
   * 检查路由权限
   */
  static checkRoute = checkRoutePermission;
  
  /**
   * 获取错误信息
   */
  static getErrorMessage = getPermissionErrorMessage;
}
